import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useEffect, useState } from "react";
import useApi from "../../../core/customhook/useApi";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import Loading from "../../../core/common/custom-loading/Loading";
import dayjs from "dayjs";
import { SearchPayloadType } from "../../admin/reusable/FiltersTrackCase";
import webservice_url from "../../../core/webservice/Webservice_url";
import Swal from "sweetalert2";

type CheckStatus =
  | "VERIFICATION_IN_PROGRESS"
  | "STOPPED"
  | "CANCELLED"
  | "COMPLETED";
interface CaseActionItemsProps {
  caseDetail?: any;
  assignmentId?: string;
}
interface TableDataInterface {
  assignment_template_id: string;
  assignment_id?: string;
  case_id?: string;
  check_name?: string;
  key_value?: string;
  status?: string;
  start_date?: string;
}

const STATUS_CONFIG: Record<
  CheckStatus,
  {
    label: string;
    bgColor: string;
    color: string;
  }
> = {
  VERIFICATION_IN_PROGRESS: {
    label: "Verification In Progress",
    bgColor: "#0D6EFD",
    color: "#fff",
  },

  STOPPED: {
    label: "Stopped",
    bgColor: "#6C757D",
    color: "#fff",
  },

  CANCELLED: {
    label: "Cancelled",
    bgColor: "#DC3545",
    color: "#fff",
  },

  COMPLETED: {
    label: "Completed",
    bgColor: "#198754",
    color: "#fff",
  },
};

const CaseActionItems: React.FC<CaseActionItemsProps> = ({ assignmentId }) => {
  const navigate = useNavigate();
  const [activeCheck, setActiveCheck] = useState(null);
  const [checkHistory, setCheckHistory] = useState<TableDataInterface[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedAddressCheck, setSelectedAddressCheck] = useState<any>(null);
  const [editedAddress, setEditedAddress] = useState("");
  const [lastSearchPayload, setLastSearchPayload] = useState<
    Partial<SearchPayloadType>
  >({});
  const [editedPincode, setEditedPincode] = useState("");
  const [total, setTotal] = useState(0);
  const [caseInfo, setCaseInfo] = useState<any>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const getChecksHistoryAPI = useApi<any>(
    assignmentId
      ? `/api/v1/candidate/forms/case-check-summary/${assignmentId}`
      : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const [caseLogs, setCaseLogs] = useState<Record<string, any[]>>({});

  const getCaseLog = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleEditAddress = async (check: any) => {
    setSelectedAddressCheck(check);
    setEditedAddress("");
    setEditedPincode("");
    setShowAddressModal(true);

    const candidateId = check?.candidate_id;

    if (!candidateId) {
      console.error("Candidate ID not found:", check);
      return;
    }

    const apiUrl = `/${webservice_url.getApplicantAddress}/address/${candidateId}`;

    console.log("Calling address API:", apiUrl);

    try {
      const res = await GetAddress.callApi(undefined, apiUrl);

      console.log("Full address API response:", res);

      const responseData =
        res?.data?.data ?? res?.data?.address ?? res?.data ?? res;

      console.log("Parsed address data:", responseData);

      setEditedAddress(
        responseData?.address ??
          responseData?.applicant_address ??
          responseData?.key_value ??
          "",
      );

      setEditedPincode(
        responseData?.pincode != null
          ? String(responseData.applicant_address_pin_code)
          : responseData?.applicant_address_pin_code != null
            ? String(responseData.applicant_address_pin_code)
            : "",
      );
    } catch (error) {
      console.error("Address API failed:", error);
    }
  };

  const handleViewLog = async (assignmentTemplateId?: string) => {
    if (!assignmentTemplateId) return;
    if (caseLogs[assignmentTemplateId]) return;

    try {
      const res = await getCaseLog.callApi(
        undefined,
        `/api/v1/candidate/forms/timeline/${assignmentTemplateId}`,
      );

      setCaseLogs((prev) => ({
        ...prev,
        [assignmentTemplateId]: res?.data || [],
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCheckHistory = async (
    searchPayload: Partial<SearchPayloadType> = {},
    page: number = pagination.page,
    limit: number = pagination.limit,
  ) => {
    if (!assignmentId) return;

    try {
      const res = await getChecksHistoryAPI.callApi();
      const historyList = Array.isArray(res?.data?.checks)
        ? res.data.checks
        : [];

      setCheckHistory(historyList);
      setTotal(historyList.length);
      setLastSearchPayload(searchPayload);

      setCaseInfo({
        case_id: res?.data?.case_id,
        candidate_name: res?.data?.candidate_name,
        case_receive_date: res?.data?.case_receive_date,
        sla_start_date: res?.data?.sla_start_date,
        due_date: res?.data?.due_date,
      });
    } catch (error) {
      console.error(error);
      setCheckHistory([]);
      setTotal(0);
      setCaseInfo({});
    }
  };

  useEffect(() => {
    if (assignmentId) {
      fetchCheckHistory();
    }
  }, [assignmentId]);

  const loader = getChecksHistoryAPI.loading;
  const formatStatus = (status: string) => {
    if (!status) return "-";

    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const GetAddress = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const UpdateAddress = useApi<any>("", {
    method: "PUT",
    autoCall: false,
    handleGlobalError: false,
  });

  const CheckTypeBadge = ({ type }: { type?: string }) => {
    if (!type) return null;
    let key = type.toUpperCase().replace(/[\s-]/g, "_");
    if (key === "NOT_ASSISTED") key = "NON_ASSISTED";
    const CHECK_TYPE_CONFIG: Record<
      string,
      { label: string; icon: string; className: string }
    > = {
      ASSISTED: {
        label: "Assisted",
        icon: "ti ti-user-check",
        className: "cai-type-assisted",
      },
      NON_ASSISTED: {
        label: "Non-Assisted",
        icon: "ti ti-bolt",
        className: "cai-type-non-assisted",
      },
    };
    const config = CHECK_TYPE_CONFIG[key] || {
      label: type,
      icon: "ti ti-tag",
      className: "cai-type-default",
    };

    return (
      <span className={`cai-type-badge ${config.className}`}>
        <i className={config.icon}></i>
        {config.label}
      </span>
    );
  };
  const hasValue = (value: unknown): value is string => {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return (
      text !== "" &&
      !["--", "-", "N/A", "NA", "null", "undefined"].includes(text)
    );
  };
  const handleCopy = async (text: string, id: string) => {
    if (!text) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopiedKey(id);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error("Copy failed", error);
    }
  };
  return (
    <>
      <style>{`
        .cai-scroll {
          -webkit-overflow-scrolling: touch;
        }
        .cai-row-card {
          border: 1px solid #eef0f3 !important;
          border-radius: 14px !important;
          transition: box-shadow 0.2s ease, transform 0.15s ease;
        }
        .cai-row-card:hover {
          box-shadow: 0 6px 18px rgba(20, 20, 43, 0.08) !important;
          transform: translateY(-1px);
        }
        .cai-col-label {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #9098a8;
          margin-bottom: 2px;
        }
        .cai-check-name {
          font-size: 0.90rem;
        }
        .cai-key-value {
          font-size: 0.8rem;
          word-break: break-word;
        }
        .cai-status-btn {
          border-radius: 25px !important;
          min-width: 160px;
          font-weight: 600 !important;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
          transition: transform 0.15s ease, box-shadow 0.15s ease !important;
        }
        .cai-status-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18) !important;
        }
        .cai-view-insuff-btn {
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.82rem;
          padding: 0.45rem 0.9rem;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cai-view-insuff-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(255, 193, 7, 0.25);
        }
        .cai-expand-card {
          border: 1px solid #eef0f3 !important;
          border-radius: 0 0 14px 14px !important;
          border-top: none !important;
          background-color: #fbfbfd;
          animation: cai-fade-in 0.2s ease;
        }
        @keyframes cai-fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cai-expand-header {
          background-color: #ffffff;
          border-bottom: 1px solid #eef0f3;
        }
        .cai-status-chip {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .cai-log-table thead th {
          font-size: 0.76rem;
          letter-spacing: 0.02em; 
          color: #6b7280;
          background-color: #f4f5f8 !important;
          border-bottom: 2px solid #e5e7eb !important;
        }
        .cai-log-table tbody tr {
          transition: background-color 0.15s ease;
        }
        .cai-log-table tbody tr:hover {
          background-color: #f8f9fc;
        }
        .cai-log-table td, .cai-log-table th {
          vertical-align: middle;
          font-size: 0.86rem;
        }
        .cai-no-timeline {
          color: #9098a8;
          font-style: italic;
          padding: 1.5rem 0;
        }
      `}</style>
      <div className="overflow-x-auto w-100 cai-scroll">
        <div
          style={{
            minWidth: "800px",
          }}
        >
          {getChecksHistoryAPI.loading ? (
            <Loading />
          ) : (
            <>
              {checkHistory?.length > 0 ? (
                [...checkHistory]
                  .sort(
                    (a: any, b: any) =>
                      ["NON-ASSISTED", "ASSISTED"].indexOf(a.check_type) -
                      ["NON-ASSISTED", "ASSISTED"].indexOf(b.check_type),
                  )
                  .map((d: any) => (
                    <div key={d.assignment_template_id} className="mb-3">
                      <div className="bg-white border cai-row-card rounded-3 py-3 px-2 shadow-sm">
                        <div className="row g-0 align-items-center">
                          {/* Check Details */}
                          <div className="col-lg-2 px-3 border-end">
                            <div className="cai-col-label">Check</div>
                            <div className="fw-bold text-dark cai-check-name">
                              {d.check_name}
                            </div>
                            <CheckTypeBadge type={d.check_type} />

                            {d.subLabel && (
                              <small className="text-muted">{d.subLabel}</small>
                            )}
                          </div>
                          <div className="col-lg-3 px-3 border-end">
                            <div className="cai-col-label">Reference</div>
                            {d.check_name === "Address Verification" ? (
                              <div className="position-relative pe-3">
                                {/* <div className="fw-semibold mb-1">
                                  Reference
                                </div> */}

                                <div className="text-break cai-key-value">
                                  {d.key_value || "-"}
                                </div>

                                <button
                                  type="button"
                                  style={{
                                    position: "absolute",
                                    top: "-25px",
                                    right: "-14px",
                                  }}
                                  // className="btn btn-sm btn-light border position-absolute top-0 end-0 m-2"
                                  className="btn btn-sm btn-light border m-2"
                                  title="Edit address"
                                  aria-label="Edit address reference"
                                  onClick={() => handleEditAddress(d)}
                                >
                                  <i className="ti ti-pencil text-danger" />
                                </button>
                              </div>
                            ) : (
                              <div>
                                {/* <div className="fw-semibold mb-1">
                                  Reference
                                </div> */}
                                <div className="text-break cai-key-value">
                                  {d.key_value || "-"}
                                </div>
                              </div>
                            )}

                            {d.detailsExtra && (
                              <small className="text-muted d-block">
                                {d.detailsExtra}
                              </small>
                            )}
                          </div>

                          {/* Timeline */}
                          <div className="col-lg-2 col-md-3 px-3 border-end">
                            <div className="cai-col-label">Timeline</div>
                            {d.status === "cancelled" ? (
                              <>
                                <div>
                                  <span className="fw-semibold">
                                    Cancelled By:
                                  </span>{" "}
                                  {d.last_updated_by}
                                </div>

                                <div>
                                  <span className="fw-semibold">Reason:</span>{" "}
                                  {d.reason}
                                </div>
                              </>
                            ) : (
                              <>
                                {d.start_date && (
                                  <div>
                                    <span className="fw-semibold">Start:</span>{" "}
                                    {dayjs(d.start_date).format("DD-MM-YYYY")}
                                  </div>
                                )}

                                {d.completed_date && (
                                  <div>
                                    <span className="fw-semibold">
                                      Completed:
                                    </span>{" "}
                                    {dayjs(d.completed_date).format(
                                      "DD-MM-YYYY",
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="col-lg-3 px-3 border-end text-center">
                            <Button
                              className="cai-status-btn"
                              style={{
                                background: d.color_value || "#6c757d",
                                color: "#fff",
                                border: "none",
                                borderRadius: "25px",
                                minWidth: "150px",
                                fontWeight: 600,
                              }}
                              onClick={() => {
                                const isOpen =
                                  activeCheck === d.assignment_template_id;

                                if (isOpen) {
                                  setActiveCheck(null);
                                } else {
                                  setActiveCheck(d.assignment_template_id);
                                  handleViewLog(d.assignment_template_id);
                                }
                              }}
                            >
                              {STATUS_CONFIG[d.status as CheckStatus]?.label ||
                                formatStatus(d.status)}

                              {activeCheck === d.assignment_template_id ? (
                                <CaretUpOutlined className="ms-2" />
                              ) : (
                                <CaretDownOutlined className="ms-2" />
                              )}
                            </Button>
                          </div>
                          <div className="col-lg-2 col-md-12 px-3 mt-3 mt-lg-0">
                            <div className="d-grid gap-2">
                              {(() => {
                                const hideActionButtons = [
                                  // "CANDIDATE_PENDING",
                                  // "CANDIDATE_IN_PROGRESS",
                                  "STOPPED",
                                ].includes(d.status);

                                const isWithoutValidation =
                                  /\(without validation\)/i.test(
                                    d.check_name ?? "",
                                  );

                                return !hideActionButtons ? (
                                  <>
                                    {(d.artifact === true ||
                                      d.artifact === "true") && (
                                      <button
                                        className="btn btn-outline-primary btn-sm fw-semibold"
                                        onClick={() => {
                                          navigate(
                                            all_routes.manager_addArtifact,
                                            {
                                              state: {
                                                case_id: d.case_id ?? "",
                                                assignment_id:
                                                  d.assignment_id ?? "",
                                                assignment_template_id:
                                                  d.assignment_template_id ??
                                                  "",
                                                check_id: d.check_id ?? "",
                                              },
                                            },
                                          );
                                        }}
                                      >
                                        <i className="ti ti-paperclip me-1"></i>
                                        Attach Artifact
                                      </button>
                                    )}

                                    {(d.insuff === true ||
                                      d.insuff === "true") &&
                                      !isWithoutValidation && (
                                        <button
                                          className="btn btn-outline-warning btn-sm fw-semibold"
                                          onClick={() =>
                                            navigate(
                                              `${all_routes.manager_RaiseInsufficiency}`,
                                              {
                                                state: {
                                                  assignment_template_id:
                                                    d.assignment_template_id,
                                                  assignment_id:
                                                    d.assignment_id,
                                                  case_id: d.case_id,
                                                  candidate_id: d.candidate_id,
                                                },
                                              },
                                            )
                                          }
                                        >
                                          <i className="ti ti-alert-triangle me-1"></i>
                                          Raise Insuff
                                        </button>
                                      )}
                                  </>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {activeCheck === d.assignment_template_id && (
                        <div className="card cai-expand-card m-0 mt-0 w-100 p-2">
                          {getCaseLog.loading ? (
                            <Loading />
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-bordered mb-0 cai-log-table">
                                <thead className="table-light">
                                  <tr>
                                    <th>Title</th>
                                    <th>Description</th>
                                    <th>Actor Name</th>
                                    <th>Actor Type</th>
                                    <th style={{ width: "20%" }}>Event Time</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(caseLogs[d.assignment_template_id] || [])
                                    .length > 0 ? (
                                    caseLogs[d.assignment_template_id].map(
                                      (item: any) => (
                                        <tr key={item.event_id}>
                                          <td>{item.title}</td>

                                          <td>{item.description}</td>

                                          <td>{item.actor_name}</td>

                                          <td>{item.actor_type}</td>

                                          <td>
                                            {dayjs(item.event_time).format(
                                              "DD MMM YYYY hh:mm A",
                                            )}
                                          </td>
                                        </tr>
                                      ),
                                    )
                                  ) : (
                                    <tr>
                                      <td colSpan={5} className="text-center">
                                        <span className="cai-no-timeline">
                                          No Timeline Found
                                        </span>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <div className="card">
                  <div className="caf-empty-state">No Data Available</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showAddressModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Applicant Address</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddressModal(false)}
                  aria-label="Close"
                />
              </div>

              <div className="modal-body">
                <label className="form-label fw-semibold">
                  Address <span className="text-danger">*</span>
                </label>

                <textarea
                  className="form-control"
                  rows={4}
                  value={editedAddress}
                  onChange={(e) => setEditedAddress(e.target.value)}
                  placeholder="Enter address"
                />

                <div className="mt-3 mb-2">
                  <label className="form-label fw-semibold">
                    Pincode <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter pincode"
                    value={editedPincode}
                    onChange={(e) =>
                      setEditedPincode(
                        e.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                    maxLength={6}
                  />
                </div>
              </div>

              <div className="modal-footer d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowAddressModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!editedAddress.trim()}
                  onClick={async () => {
                    const applicant_address = editedAddress.trim();
                    const applicant_address_pin_code = editedPincode.trim();
                    const candidateId = selectedAddressCheck?.candidate_id;

                    if (!applicant_address) {
                      Swal.fire(
                        "Validation Error",
                        "Please enter the address.",
                        "warning",
                      );
                      return;
                    }

                    if (!/^\d{6}$/.test(applicant_address_pin_code)) {
                      Swal.fire(
                        "Validation Error",
                        "Please enter a valid 6-digit pincode.",
                        "warning",
                      );
                      return;
                    }

                    if (!candidateId) {
                      Swal.fire("Error", "Candidate ID not found.", "error");
                      return;
                    }

                    try {
                      Swal.fire({
                        title: "Updating Address...",
                        text: "Please wait while we update the address.",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        didOpen: () => {
                          Swal.showLoading();
                        },
                      });

                      const payload = {
                        applicant_address,
                        applicant_address_pin_code,
                      };

                      const res = await UpdateAddress.callApi(
                        payload,
                        `/${webservice_url.getApplicantAddress}/${candidateId}/address`,
                      );



                      setCheckHistory((prev) =>
                        prev.map((item: any) =>
                          item.assignment_template_id ===
                          selectedAddressCheck.assignment_template_id
                            ? {
                                ...item,
                                key_value: applicant_address,
                                applicant_address_pin_code,
                              }
                            : item,
                        ),
                      );

                      setSelectedAddressCheck((prev: any) =>
                        prev
                          ? {
                              ...prev,
                              key_value: applicant_address,
                              applicant_address_pin_code,
                            }
                          : prev,
                      );

                      setShowAddressModal(false);
                      fetchCheckHistory();

                      await Swal.fire({
                        title: "Success!",
                        text: "Applicant address updated successfully.",
                        icon: "success",
                        confirmButtonText: "OK",
                      });
                    } catch (error: any) {
                      console.error("Failed to update address:", error);

                      Swal.fire({
                        title: "Update Failed",
                        text:
                          error?.response?.data?.message ||
                          error?.message ||
                          "Unable to update applicant address. Please try again.",
                        icon: "error",
                        confirmButtonText: "OK",
                      });
                    }
                  }}
                >
                  Save Address
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaseActionItems;

// import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
// import { Button } from "antd";
// import React, { useEffect, useState } from "react";
// import useApi from "../../../core/customhook/useApi";
// import { useNavigate } from "react-router";
// import { all_routes } from "../../router/all_routes";
// import Loading from "../../../core/common/custom-loading/Loading";
// import dayjs from "dayjs";
// import { SearchPayloadType } from "../../admin/reusable/FiltersTrackCase";
// import webservice_url from "../../../core/webservice/Webservice_url";

// type CheckStatus =
//   | "VERIFICATION_IN_PROGRESS"
//   | "STOPPED"
//   | "CANCELLED"
//   | "COMPLETED";
// interface CaseActionItemsProps {
//   caseDetail?: any;
//   assignmentId?: string;
// }
// interface TableDataInterface {
//   assignment_template_id: string;
//   assignment_id?: string;
//   case_id?: string;
//   check_name?: string;
//   key_value?: string;
//   status?: string;
//   start_date?: string;
// }

// const STATUS_CONFIG: Record<
//   CheckStatus,
//   {
//     label: string;
//     bgColor: string;
//     color: string;
//   }
// > = {
//   VERIFICATION_IN_PROGRESS: {
//     label: "Verification In Progress",
//     bgColor: "#0D6EFD",
//     color: "#fff",
//   },

//   STOPPED: {
//     label: "Stopped",
//     bgColor: "#6C757D",
//     color: "#fff",
//   },

//   CANCELLED: {
//     label: "Cancelled",
//     bgColor: "#DC3545",
//     color: "#fff",
//   },

//   COMPLETED: {
//     label: "Completed",
//     bgColor: "#198754",
//     color: "#fff",
//   },
// };

// const CaseActionItems: React.FC<CaseActionItemsProps> = ({ assignmentId }) => {
//   const navigate = useNavigate();
//   const [activeCheck, setActiveCheck] = useState(null);
//   const [checkHistory, setCheckHistory] = useState<TableDataInterface[]>([]);
//   const [pagination, setPagination] = useState({
//     page: 1,
//     limit: 10,
//   });
//   const [lastSearchPayload, setLastSearchPayload] = useState<
//     Partial<SearchPayloadType>
//   >({});
//   const [total, setTotal] = useState(0);
//   const [caseInfo, setCaseInfo] = useState<any>({});
//   const [copiedKey, setCopiedKey] = useState<string | null>(null);

//   const getChecksHistoryAPI = useApi<any>(
//     assignmentId
//       ? `/api/v1/candidate/forms/case-check-summary/${assignmentId}`
//       : "",
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const [caseLogs, setCaseLogs] = useState<Record<string, any[]>>({});
//   const getCaseLog = useApi<any>("", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const handleViewLog = async (assignmentTemplateId?: string) => {
//     if (!assignmentTemplateId) return;
//     if (caseLogs[assignmentTemplateId]) return;

//     try {
//       const res = await getCaseLog.callApi(
//         undefined,
//         `/api/v1/candidate/forms/timeline/${assignmentTemplateId}`,
//       );

//       setCaseLogs((prev) => ({
//         ...prev,
//         [assignmentTemplateId]: res?.data || [],
//       }));
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const fetchCheckHistory = async (
//     searchPayload: Partial<SearchPayloadType> = {},
//     page: number = pagination.page,
//     limit: number = pagination.limit,
//   ) => {
//     if (!assignmentId) return;

//     try {
//       const res = await getChecksHistoryAPI.callApi();
//       const historyList = Array.isArray(res?.data?.checks)
//         ? res.data.checks
//         : [];

//       setCheckHistory(historyList);
//       setTotal(historyList.length);
//       setLastSearchPayload(searchPayload);

//       setCaseInfo({
//         case_id: res?.data?.case_id,
//         candidate_name: res?.data?.candidate_name,
//         case_receive_date: res?.data?.case_receive_date,
//         sla_start_date: res?.data?.sla_start_date,
//         due_date: res?.data?.due_date,
//       });
//     } catch (error) {
//       console.error(error);
//       setCheckHistory([]);
//       setTotal(0);
//       setCaseInfo({});
//     }
//   };

//   useEffect(() => {
//     if (assignmentId) {
//       fetchCheckHistory();
//     }
//   }, [assignmentId]);

//   const loader = getChecksHistoryAPI.loading;
//   const formatStatus = (status: string) => {
//     if (!status) return "-";

//     return status
//       .toLowerCase()
//       .split("_")
//       .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
//       .join(" ");
//   };
//   const DownloadAssestedReport = useApi<any>("", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//     responseType: "blob",
//   });

//   const handleDownloadReport = async (assignmentTemplateId: any) => {
//     if (!assignmentTemplateId) {
//       console.error("Assignment Template ID is missing");
//       return;
//     }

//     try {
//       const response: any = await DownloadAssestedReport.callApi(
//         undefined,
//         `${webservice_url.DownloadAssestedReport}/${assignmentTemplateId}/download-pdf`,
//       );

//       const blob = new Blob([response], {
//         type: "application/pdf",
//       });

//       const url = window.URL.createObjectURL(blob);

//       const link = document.createElement("a");
//       link.href = url;
//       link.download = `_Report_${assignmentTemplateId}.pdf`;

//       document.body.appendChild(link);
//       link.click();

//       document.body.removeChild(link);
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       console.log("Report Download Error:", error);
//     }
//   };

//   const CheckTypeBadge = ({ type }: { type?: string }) => {
//     if (!type) return null;
//     let key = type.toUpperCase().replace(/[\s-]/g, "_");
//     if (key === "NOT_ASSISTED") key = "NON_ASSISTED";
//     const CHECK_TYPE_CONFIG: Record<
//       string,
//       { label: string; icon: string; className: string }
//     > = {
//       ASSISTED: {
//         label: "Assisted",
//         icon: "ti ti-user-check",
//         className: "cai-type-assisted",
//       },
//       NON_ASSISTED: {
//         label: "Non-Assisted",
//         icon: "ti ti-bolt",
//         className: "cai-type-non-assisted",
//       },
//     };
//     const config = CHECK_TYPE_CONFIG[key] || {
//       label: type,
//       icon: "ti ti-tag",
//       className: "cai-type-default",
//     };

//     return (
//       <span className={`cai-type-badge ${config.className}`}>
//         <i className={config.icon}></i>
//         {config.label}
//       </span>
//     );
//   };
//   const hasValue = (value: unknown): value is string => {
//     if (value === null || value === undefined) return false;
//     const text = String(value).trim();
//     return (
//       text !== "" &&
//       !["--", "-", "N/A", "NA", "null", "undefined"].includes(text)
//     );
//   };
//   const handleCopy = async (text: string, id: string) => {
//     if (!text) return;

//     try {
//       if (navigator.clipboard && window.isSecureContext) {
//         await navigator.clipboard.writeText(text);
//       } else {
//         const textarea = document.createElement("textarea");
//         textarea.value = text;
//         textarea.style.position = "fixed";
//         textarea.style.opacity = "0";
//         document.body.appendChild(textarea);
//         textarea.select();
//         document.execCommand("copy");
//         document.body.removeChild(textarea);
//       }

//       setCopiedKey(id);
//       setTimeout(() => setCopiedKey(null), 2000);
//     } catch (error) {
//       console.error("Copy failed", error);
//     }
//   };
//   return (
//     <>
//       <style>{`
//         .cai-scroll {
//           -webkit-overflow-scrolling: touch;
//         }
//         .cai-row-card {
//           border: 1px solid #eef0f3 !important;
//           border-radius: 14px !important;
//           transition: box-shadow 0.2s ease, transform 0.15s ease;
//         }
//         .cai-row-card:hover {
//           box-shadow: 0 6px 18px rgba(20, 20, 43, 0.08) !important;
//           transform: translateY(-1px);
//         }
//         .cai-col-label {
//           font-size: 0.72rem;
//           font-weight: 700;
//           letter-spacing: 0.04em;
//           text-transform: uppercase;
//           color: #9098a8;
//           margin-bottom: 2px;
//         }
//         .cai-check-name {
//           font-size: 0.90rem;
//         }
//         .cai-key-value {
//           font-size: 0.8rem;
//           word-break: break-word;
//         }
//         .cai-status-btn {
//           border-radius: 25px !important;
//           min-width: 160px;
//           font-weight: 600 !important;
//           box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
//           transition: transform 0.15s ease, box-shadow 0.15s ease !important;
//         }
//         .cai-status-btn:hover {
//           transform: translateY(-1px);
//           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18) !important;
//         }
//         .cai-view-insuff-btn {
//           border-radius: 8px;
//           font-weight: 600;
//           font-size: 0.82rem;
//           padding: 0.45rem 0.9rem;
//           transition: transform 0.15s ease, box-shadow 0.15s ease;
//         }
//         .cai-view-insuff-btn:hover {
//           transform: translateY(-1px);
//           box-shadow: 0 3px 10px rgba(255, 193, 7, 0.25);
//         }
//         .cai-expand-card {
//           border: 1px solid #eef0f3 !important;
//           border-radius: 0 0 14px 14px !important;
//           border-top: none !important;
//           background-color: #fbfbfd;
//           animation: cai-fade-in 0.2s ease;
//         }
//         @keyframes cai-fade-in {
//           from { opacity: 0; transform: translateY(-4px); }
//           to { opacity: 1; transform: translateY(0); }
//         }
//         .cai-expand-header {
//           background-color: #ffffff;
//           border-bottom: 1px solid #eef0f3;
//         }
//         .cai-status-chip {
//           padding: 6px 14px;
//           border-radius: 20px;
//           font-size: 0.8rem;
//           font-weight: 600;
//         }
//         .cai-log-table thead th {
//           font-size: 0.76rem;
//           letter-spacing: 0.02em;
//           color: #6b7280;
//           background-color: #f4f5f8 !important;
//           border-bottom: 2px solid #e5e7eb !important;
//         }
//         .cai-log-table tbody tr {
//           transition: background-color 0.15s ease;
//         }
//         .cai-log-table tbody tr:hover {
//           background-color: #f8f9fc;
//         }
//         .cai-log-table td, .cai-log-table th {
//           vertical-align: middle;
//           font-size: 0.86rem;
//         }
//         .cai-no-timeline {
//           color: #9098a8;
//           font-style: italic;
//           padding: 1.5rem 0;
//         }
//       `}</style>
//       <div className="overflow-x-auto w-100 cai-scroll">
//         <div
//           style={{
//             minWidth: "800px",
//           }}
//         >
//           {getChecksHistoryAPI.loading ? (
//             <Loading />
//           ) : (
//             <>
//               {checkHistory?.length > 0 ? (
//                 [...checkHistory]
//                   .sort(
//                     (a: any, b: any) =>
//                       ["NON-ASSISTED", "ASSISTED"].indexOf(a.check_type) -
//                       ["NON-ASSISTED", "ASSISTED"].indexOf(b.check_type),
//                   )
//                   .map((d: any) => (
//                     <div key={d.assignment_template_id} className="mb-3">
//                       <div className="bg-white border cai-row-card rounded-3 py-3 px-2 shadow-sm">
//                         <div className="row g-0 align-items-center">
//                           {/* Check Details */}
//                           <div className="col-lg-2 px-3 border-end">
//                             <div className="cai-col-label">Check</div>
//                             <div className="fw-bold text-dark cai-check-name">
//                               {d.check_name}
//                             </div>
//                             <CheckTypeBadge type={d.check_type} />

//                             {d.subLabel && (
//                               <small className="text-muted">{d.subLabel}</small>
//                             )}
//                           </div>
//                           <div className="col-lg-3 px-3 border-end">
//                             <div className="cai-col-label">Reference</div>

//                             {hasValue(d.key_value) ? (
//                               <div className="d-flex align-items-center gap-2">
//                                 <div className="fw-semibold cai-key-value text-break">
//                                   {d.key_value}
//                                 </div>

//                                 <button
//                                   type="button"
//                                   className="btn btn-sm p-0 border-0 bg-transparent"
//                                   title={
//                                     copiedKey === d.key_value
//                                       ? "Copied!"
//                                       : "Copy"
//                                   }
//                                   onClick={() =>
//                                     handleCopy(
//                                       String(d.key_value).trim(),
//                                       d.key_value,
//                                     )
//                                   }
//                                   style={{ lineHeight: 1 }}
//                                 >
//                                   <i
//                                     className={
//                                       copiedKey === d.key_value
//                                         ? "ti ti-check text-success"
//                                         : "ti ti-copy text-muted"
//                                     }
//                                     style={{ fontSize: 16 }}
//                                   ></i>
//                                 </button>
//                               </div>
//                             ) : (
//                               <div className="text-muted">--</div>
//                             )}

//                             {d.detailsExtra && (
//                               <small className="text-muted d-block">
//                                 {d.detailsExtra}
//                               </small>
//                             )}
//                           </div>

//                           {/* Timeline */}
//                           <div className="col-lg-2 col-md-3 px-3 border-end">
//                             <div className="cai-col-label">Timeline</div>
//                             {d.status === "cancelled" ? (
//                               <>
//                                 <div>
//                                   <span className="fw-semibold">
//                                     Cancelled By:
//                                   </span>{" "}
//                                   {d.last_updated_by}
//                                 </div>

//                                 <div>
//                                   <span className="fw-semibold">Reason:</span>{" "}
//                                   {d.reason}
//                                 </div>
//                               </>
//                             ) : (
//                               <>
//                                 {d.start_date && (
//                                   <div>
//                                     <span className="fw-semibold">Start:</span>{" "}
//                                     {dayjs(d.start_date).format("DD-MM-YYYY")}
//                                   </div>
//                                 )}

//                                 {d.completed_date && (
//                                   <div>
//                                     <span className="fw-semibold">
//                                       Completed:
//                                     </span>{" "}
//                                     {dayjs(d.completed_date).format(
//                                       "DD-MM-YYYY",
//                                     )}
//                                   </div>
//                                 )}
//                               </>
//                             )}
//                           </div>
//                           <div className="col-lg-3 px-3 border-end text-center">
//                             <Button
//                               className="cai-status-btn"
//                               style={{
//                                 background: d.color_value || "#6c757d",
//                                 color: "#fff",
//                                 border: "none",
//                                 borderRadius: "25px",
//                                 minWidth: "150px",
//                                 fontWeight: 600,
//                               }}
//                               onClick={() => {
//                                 const isOpen =
//                                   activeCheck === d.assignment_template_id;

//                                 if (isOpen) {
//                                   setActiveCheck(null);
//                                 } else {
//                                   setActiveCheck(d.assignment_template_id);
//                                   handleViewLog(d.assignment_template_id);
//                                 }
//                               }}
//                             >
//                               {STATUS_CONFIG[d.status as CheckStatus]?.label ||
//                                 formatStatus(d.status)}

//                               {activeCheck === d.assignment_template_id ? (
//                                 <CaretUpOutlined className="ms-2" />
//                               ) : (
//                                 <CaretDownOutlined className="ms-2" />
//                               )}
//                             </Button>
//                           </div>
//                           <div className="col-lg-2 col-md-12 px-3 mt-3 mt-lg-0">
//                             <div className="d-grid gap-2">
//                               {(() => {
//                                 const hideActionButtons = [
//                                   // "CANDIDATE_PENDING",
//                                   // "CANDIDATE_IN_PROGRESS",
//                                   "STOPPED",
//                                 ].includes(d.status);

//                                 const isWithoutValidation = /\(without validation\)/i.test(d.check_name ?? "",
//                                   );

//                                 return !hideActionButtons ? (
//                                   <>
//                                     {(d.artifact === true ||
//                                       d.artifact === "true") && (
//                                       <button
//                                         className="btn btn-outline-primary btn-sm fw-semibold"
//                                         onClick={() => {
//                                           navigate(
//                                             all_routes.manager_addArtifact,
//                                             {
//                                               state: {
//                                                 case_id: d.case_id ?? "",
//                                                 assignment_id:
//                                                   d.assignment_id ?? "",
//                                                 assignment_template_id:
//                                                   d.assignment_template_id ??
//                                                   "",
//                                                 check_id: d.check_id ?? "",
//                                               },
//                                             },
//                                           );
//                                         }}
//                                       >
//                                         <i className="ti ti-paperclip me-1"></i>
//                                         Attach Artifact
//                                       </button>
//                                     )}

//                                     {(d.insuff === true ||
//                                       d.insuff === "true") &&
//                                       !isWithoutValidation && (
//                                         <button
//                                           className="btn btn-outline-warning btn-sm fw-semibold"
//                                           onClick={() =>
//                                             navigate(
//                                               `${all_routes.manager_RaiseInsufficiency}`,
//                                               {
//                                                 state: {
//                                                   assignment_template_id:
//                                                     d.assignment_template_id,
//                                                   assignment_id:
//                                                     d.assignment_id,
//                                                   case_id: d.case_id,
//                                                   candidate_id: d.candidate_id,
//                                                 },
//                                               },
//                                             )
//                                           }
//                                         >
//                                           <i className="ti ti-alert-triangle me-1"></i>
//                                           Raise Insuff
//                                         </button>
//                                       )}
//                                   </>
//                                 ) : null;
//                               })()}
//                             </div>
//                           </div>
//                         </div>
//                       </div>

//                       {activeCheck === d.assignment_template_id && (
//                         <div className="card cai-expand-card m-0 mt-0 w-100 p-2">
//                           {getCaseLog.loading ? (
//                             <Loading />
//                           ) : (
//                             <div className="table-responsive">
//                               <table className="table table-bordered mb-0 cai-log-table">
//                                 <thead className="table-light">
//                                   <tr>
//                                     <th>Title</th>
//                                     <th>Description</th>
//                                     <th>Actor Name</th>
//                                     <th>Actor Type</th>
//                                     <th style={{ width: "20%" }}>Event Time</th>
//                                   </tr>
//                                 </thead>
//                                 <tbody>
//                                   {(caseLogs[d.assignment_template_id] || [])
//                                     .length > 0 ? (
//                                     caseLogs[d.assignment_template_id].map(
//                                       (item: any) => (
//                                         <tr key={item.event_id}>
//                                           <td>{item.title}</td>

//                                           <td>{item.description}</td>

//                                           <td>{item.actor_name}</td>

//                                           <td>{item.actor_type}</td>

//                                           <td>
//                                             {dayjs(item.event_time).format(
//                                               "DD MMM YYYY hh:mm A",
//                                             )}
//                                           </td>
//                                         </tr>
//                                       ),
//                                     )
//                                   ) : (
//                                     <tr>
//                                       <td colSpan={5} className="text-center">
//                                         <span className="cai-no-timeline">
//                                           No Timeline Found
//                                         </span>
//                                       </td>
//                                     </tr>
//                                   )}
//                                 </tbody>
//                               </table>
//                             </div>
//                           )}
//                         </div>
//                       )}
//                     </div>
//                   ))
//               ) : (
//                 <div className="card">
//                   <div className="caf-empty-state">No Data Available</div>
//                 </div>
//               )}
//             </>
//           )}
//         </div>
//       </div>
//     </>
//   );
// };

// export default CaseActionItems;
